import React, { useState, useEffect, useRef, useCallback } from 'react'

interface SearchBarProps {
  onSearch: (q: string) => void
  placeholder?: string
  initialValue?: string
}

export const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  placeholder = 'search posts...',
  initialValue = '',
}) => {
  const [value, setValue] = useState(initialValue)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Update if initialValue changes (e.g. from URL)
  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  // Debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      onSearch(value)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // Global '/' shortcut focuses the input
  const handleGlobalKey = useCallback((e: KeyboardEvent) => {
    const target = e.target as HTMLElement
    if (
      e.key === '/' &&
      target.tagName !== 'INPUT' &&
      target.tagName !== 'TEXTAREA' &&
      !target.isContentEditable
    ) {
      e.preventDefault()
      inputRef.current?.focus()
    }
  }, [])

  useEffect(() => {
    window.addEventListener('keydown', handleGlobalKey)
    return () => window.removeEventListener('keydown', handleGlobalKey)
  }, [handleGlobalKey])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      onSearch(value)
    }
    if (e.key === 'Escape') {
      inputRef.current?.blur()
    }
  }

  return (
    <>
      <style>{`
        .search-bar-wrapper {
          display: flex;
          align-items: center;
          background-color: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 4px;
          padding: 0 10px;
          gap: 6px;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .search-bar-wrapper:focus-within {
          border-color: var(--accent-green);
          box-shadow: 0 0 0 2px rgba(57, 255, 20, 0.12);
        }
        .search-bar-prompt {
          color: var(--accent-green);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.85rem;
          user-select: none;
          flex-shrink: 0;
        }
        .search-bar-input {
          background: transparent;
          border: none;
          outline: none;
          color: var(--text-primary);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.85rem;
          width: 100%;
          padding: 7px 0;
          caret-color: var(--accent-green);
        }
        .search-bar-input::placeholder {
          color: var(--text-muted, #555);
        }
        .search-bar-hint {
          font-size: 0.7rem;
          color: var(--text-muted, #555);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          flex-shrink: 0;
          border: 1px solid var(--border-color);
          border-radius: 2px;
          padding: 1px 4px;
        }
      `}</style>
      <div className="search-bar-wrapper">
        <span className="search-bar-prompt" aria-hidden="true">&gt;_</span>
        <input
          ref={inputRef}
          className="search-bar-input"
          type="search"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Search posts"
          autoComplete="off"
          spellCheck={false}
        />
        <span className="search-bar-hint" aria-hidden="true">/</span>
      </div>
    </>
  )
}
